import { QueryClient, useQuery } from '@tanstack/react-query'
import { AuthContext } from '@urbanx/agx.auth.client'
import { KeyMetricMetricType } from '@urbanx/agx.rex.client'
import dayjs from 'dayjs'
import { useCallback, useContext } from 'react'
import RexService from 'services/rexService'

export interface ExecutiveKeyMetricsResponse {
  period: string
  metrics: KeyMetric[]
}

export interface KeyMetric {
  title: string
  settled: number
  projectedSettled: number
  budget: number
  variance: number
  metricType: KeyMetricMetricType
}

type GetExecutiveKeyMetricsKey = [
  'GetExecutiveKeyMetrics',
  string, // month
]

export const invalidateExecutiveMetricsQuery = async (
  queryClient: QueryClient
) => {
  await queryClient.invalidateQueries({
    queryKey: ['GetExecutiveKeyMetrics'],
  })
}

const useExecutiveKeyMetrics = (month: string, enabled: boolean) => {
  const { getAuthToken } = useContext(AuthContext)

  const getExecutiveKeyMetrics = useCallback(
    async ([
      url,
      month,
    ]: GetExecutiveKeyMetricsKey): Promise<ExecutiveKeyMetricsResponse> => {
      try {
        const token = await getAuthToken()

        const service = new RexService(token)

        const result = await service.rexService.get(url, {
          month: dayjs(month).format('YYYY-MM'),
        })

        return result.data
      } catch (error) {
        console.error('Error fetching executive key metrics:', error)
        throw new Error(
          `Failed to fetch executive key metrics for month: ${month}`
        )
      }
    },
    [getAuthToken]
  )

  const { data, isPending, isError } = useQuery<
    ExecutiveKeyMetricsResponse,
    Error,
    ExecutiveKeyMetricsResponse,
    GetExecutiveKeyMetricsKey
  >({
    queryKey: ['GetExecutiveKeyMetrics', month],
    queryFn: (queryInfo) => getExecutiveKeyMetrics(queryInfo.queryKey),
    enabled: enabled,
  })

  return { data, isPending, isError }
}

export default useExecutiveKeyMetrics
